// 대화 첨부 처리 — 파일→첨부 항목 변환, RAG 색인 진행 반영, 첨부 바 렌더
import React from 'react';
import { uploadDocs } from '../../api/aiChat';
import { isImageFile } from '../../utils/image';
import type { ConversationAttachment } from './Home.types';
import styles from './Home.startview.module.css';

let attachUidSeq = 0;
export const newAttachmentUid = () => `att-${++attachUidSeq}`;

/* 선택한 파일을 첨부 항목으로 — 색인 시작 전이므로 대기 상태로 만든다 */
export function toAttachments(rawFiles: File[]): ConversationAttachment[] {
  return rawFiles.map(f => ({
    uid: newAttachmentUid(),
    name: f.name,
    kind: isImageFile(f) ? ('image' as const) : ('doc' as const),
    // 동시 업로드는 몇 건까지라, 한도를 넘긴 파일은 차례를 기다린다 — 시작 문구는 '대기 중'
    ingest: { status: 'processing' as const, label: '업로드 대기 중', percent: 0 },
  }));
}

/**
 * 첨부 파일 RAG 색인 — ingestion-service가 SSE로 내리는 단계(파싱→분할→임베딩→색인)를
 * 칩에 그대로 표시한다. 실패해도 첨부 칩 자체는 남기고 사유만 칩에 띄운다.
 */
export function ingestAttachments(
  rawFiles: File[],
  attached: ConversationAttachment[],
  conversationId: string,
  patch: (uid: string, ingest: ConversationAttachment['ingest'], docId?: string) => void,
) {
  uploadDocs(rawFiles, conversationId, {
    onProgress: (index, p) => {
      const uid = attached[index]?.uid;
      if (!uid) return;
      patch(uid, {
        status: p.step === 'error' ? 'error' : p.step === 'done' ? 'done' : 'processing',
        label: p.label,
        percent: p.percent,
      }, p.docId);
    },
  }).catch(e => {
    // uploadDocs는 파일별 실패를 결과에 담으므로, 여기 오는 건 그 바깥의 예기치 못한 오류
    const reason = e instanceof Error && /[가-힣]/.test(e.message) ? e.message : '일시적인 문제로 처리하지 못했습니다';
    attached.forEach(f => patch(f.uid, { status: 'error', label: `검색 준비 실패 — ${reason}`, percent: 100 }));
  });
}

/**
 * 대화 첨부 바 — 이 대화에 붙어 있는 자료(문서·이미지)를 한 줄로 보여준다.
 *
 * 전송해도 사라지지 않는다: 색인된 자료는 대화 내내 검색 대상이라 특정 말풍선에 묶이지
 * 않기 때문이다. × 를 누르면 목록에서 빠지고 색인도 함께 내려간다.
 */
export function AttachmentStrip({
  attachments,
  notice,
  onRemove,
}: {
  attachments: ConversationAttachment[];
  notice: string | null;
  onRemove: (uid: string) => void;
}) {
  if (attachments.length === 0 && !notice) {
    return null;
  }
  return (
    <div className={styles.attachBar}>
      {attachments.length > 0 && (
        <div className={styles.attachList}>
          {attachments.map(a => {
            const failed = a.ingest?.status === 'error';
            const busy = a.ingest?.status === 'processing';
            return (
              <div
                key={a.uid}
                className={`${styles.attachItem} ${failed ? styles.attachItemError : ''}`}
                title={a.ingest?.label ?? a.name}
              >
                {a.previewUrl ? (
                  <img src={a.previewUrl} alt={a.name} className={styles.attachThumb} />
                ) : (
                  <span className={styles.attachIcon}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
                    </svg>
                  </span>
                )}
                {/* 이름 아래에 상태를 둔다 — 옆에 붙이면 "임베딩 8/40 조각" 같은 문구에
                    칩이 가로로 길어져 여러 개를 나란히 두기 어렵다.
                    진행·실패만 알린다: 완료는 목록에 있다는 것 자체가 표시다. */}
                <span className={styles.attachBody}>
                  <span className={styles.attachName}>{a.name}</span>
                  {busy && (
                    <>
                      <span className={styles.attachState}>{a.ingest?.label}</span>
                      <span className={styles.attachTrack}>
                        <span
                          className={styles.attachFill}
                          style={{ width: `${Math.max(0, Math.min(100, a.ingest?.percent ?? 0))}%` }}
                        />
                      </span>
                    </>
                  )}
                  {failed && <span className={styles.attachStateError}>{a.ingest?.label}</span>}
                </span>
                <button
                  type="button"
                  className={styles.attachRemove}
                  aria-label={`${a.name} 첨부 삭제`}
                  onClick={() => onRemove(a.uid)}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
      {notice && <div className={styles.attachNotice}>{notice}</div>}
    </div>
  );
}
